// Cache management utilities for better cache invalidation

export const CACHE_VERSION_KEY = 'hogcloud-cache-version';

/**
 * Get the current cache version from localStorage
 */
export const getCurrentCacheVersion = (): string | null => {
  return localStorage.getItem(CACHE_VERSION_KEY);
};

/**
 * Set the cache version in localStorage
 */
export const setCacheVersion = (version: string): void => {
  localStorage.setItem(CACHE_VERSION_KEY, version);
};

/**
 * Check if there's a new version available and force refresh if needed
 */
export const checkForUpdates = (): void => {
  // Only run cache management in production
  if (process.env.NODE_ENV !== 'production') {
    return;
  }
  
  // Get the build timestamp from the current script
  const buildTime = process.env.REACT_APP_BUILD_TIME;
  
  // If no build time is set, skip cache management
  if (!buildTime) {
    return;
  }
  
  const currentVersion = getCurrentCacheVersion();
  
  if (!currentVersion || currentVersion !== buildTime) {
    console.log('New version detected, forcing cache refresh');
    setCacheVersion(buildTime);
    
    // Force service worker update
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        registration.update().then(() => {
          // Send message to service worker to skip waiting
          if (registration.waiting) {
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          }
        });
      });
    }
    
    // Force page reload to get fresh content
    window.location.reload();
  }
};

/**
 * Clear all caches and force fresh load
 */
export const clearAllCaches = (): Promise<void> => {
  return new Promise((resolve) => {
    if ('caches' in window) {
      caches.keys().then(cacheNames => {
        const deletePromises = cacheNames.map(cacheName => {
          console.log('Deleting cache:', cacheName);
          return caches.delete(cacheName);
        });
        
        Promise.all(deletePromises).then(() => {
          localStorage.removeItem(CACHE_VERSION_KEY);
          console.log('All caches cleared');
          resolve();
        });
      });
    } else {
      resolve();
    }
  });
};

/**
 * Initialize cache management on app startup
 */
export const initializeCacheManager = (): void => {
  // Check for updates when the app loads
  checkForUpdates();
  
  // Only set up service worker listeners in production
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('Service worker updated, reloading page');
      window.location.reload();
    });
  }
  
  // Add a global function for manual cache clearing (useful for debugging)
  (window as any).clearAppCache = clearAllCaches;
};
